import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useAllEmployees } from '~/data/employees';
import { Enum, useAllEnums } from '~/data/enums';

import { useSxStyles } from '../common/useStyles';

import { EmplOtherHistoryType } from './data';

export type DataType = {
  data: EmplOtherHistoryType[];
};

type EmplOtherHistoryRow = EmplOtherHistoryType & {
  id: string;
};

export const EmplOtherHistory = (props: DataType) => {
  const { data: emplOtherHistory } = props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { allEnums } = useAllEnums();
  const { getEmployeeById } = useAllEmployees();

  const jsonTrialPeriodOptions = [
    { code: 'P1M', name: `1 ${t('laMonth')}` },
    { code: 'P2M', name: `2 ${t('laMonths')}` },
    { code: 'P3M', name: `3 ${t('laMonths')}` },
    { code: 'P4M', name: `4 ${t('laMonths')}` },
    { code: 'P5M', name: `5 ${t('laMonths')}` },
    { code: 'P6M', name: `6 ${t('laMonths')}` },
  ];

  const getTrialPeriod = (id: string) => {
    if (id === undefined || id === '') return '';
    const item = jsonTrialPeriodOptions.find((en) => en.code === id);
    return item === undefined ? id : item.name;
  };

  const getEnumName = (enumValue: Enum[], id: string) => {
    if (id === undefined || id === '') return '';
    const specificEnum = enumValue.find((en) => en.code === id);
    return specificEnum === undefined ? id : specificEnum.name;
  };

  const getEmployeeNameById = (employeeId?: number) => {
    const employee = getEmployeeById(employeeId);
    return employee
      ? `${employee.fEmpLastName}, ${employee.fEmpFirstName}`
      : '';
  };

  const rows: EmplOtherHistoryRow[] = emplOtherHistory.map((row, index) => ({
    ...row,
    id: `${row.fEmplDateStart}-${row.fEmplType}-${index}`,
  }));

  const columns: GridColDef<EmplOtherHistoryRow>[] = [
    {
      field: 'fEmplDateStart',
      headerName: t('laStartDate'),
      minWidth: 130,
      flex: 1,
      valueGetter: (_value, row) =>
        moment(row.fEmplDateStart).format('DD.MM.YYYY'),
    },
    {
      field: 'fEmplType',
      headerName: t('laEmploymentType'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        getEnumName(allEnums.EMPLOYMENT_TYPE, row.fEmplType),
    },
    {
      field: 'fEmplCurrency',
      headerName: t('laCurrency'),
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'fEmplTrialPeriod',
      headerName: t('fTrialPeriod'),
      minWidth: 140,
      flex: 1,
      valueGetter: (_value, row) => getTrialPeriod(row.fEmplTrialPeriod),
    },
    {
      field: 'fEmplTrialPeriodEndDate',
      headerName: t('laTrialPeriodEndDate'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        row.fEmplTrialPeriodEndDate
          ? moment(row.fEmplTrialPeriodEndDate).format('DD.MM.YYYY')
          : '',
    },
    {
      field: 'fEmplLastDateEmployment',
      headerName: t('fLastDEmpl'),
      minWidth: 150,
      flex: 1,
      valueGetter: (_value, row) =>
        row.fEmplLastDateEmployment
          ? moment(row.fEmplLastDateEmployment).format('DD.MM.YYYY')
          : '',
    },
    {
      field: 'fEmplTerminationDate',
      headerName: t('fLastDTemp'),
      minWidth: 150,
      flex: 1,
      valueGetter: (_value, row) =>
        row.fEmplTerminationDate
          ? moment(row.fEmplTerminationDate).format('DD.MM.YYYY')
          : '',
    },
    {
      field: 'fEmplTerminationReason',
      headerName: t('laTerminationReason'),
      minWidth: 180,
      flex: 1,
      valueGetter: (_value, row) =>
        getEnumName(
          allEnums.EMPL_TERMINATION_REASON,
          row.fEmplTerminationReason,
        ),
    },
    {
      field: 'fEmplFixedTermReason',
      headerName: t('fReasonFixTerm'),
      minWidth: 180,
      flex: 1,
      valueGetter: (_value, row) =>
        getEnumName(allEnums.EMPL_FIXED_TERM_REASON, row.fEmplFixedTermReason),
    },
    {
      field: 'fEmplPlannedEndDate',
      headerName: t('fEmplPlannedEndDate'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        row.fEmplPlannedEndDate
          ? moment(row.fEmplPlannedEndDate).format('DD.MM.YYYY')
          : '',
    },
    {
      field: 'fEmplInfo',
      headerName: t('fEmplInfo'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) => row.fEmplInfo ?? '',
    },
    {
      field: 'fEmplEnteredTime',
      headerName: t('laUpdated'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        moment(row.fEmplEnteredTime).format('DD.MM.YYYY HH:mm'),
    },
    {
      field: 'fEmplEnteredUser',
      headerName: t('laUpdatedBy'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) => getEmployeeNameById(row.fEmplEnteredUser),
    },
  ];

  return (
    <DataGridPremium
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      disableColumnMenu={true}
      disableRowSelectionOnClick={true}
      getRowHeight={() => 'auto'}
      initialState={{ density: 'compact' }}
      localeText={{ noRowsLabel: t('laNoHistoryNow') }}
      sx={sxStyles.dataGrid}
    />
  );
};
